import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  CreateInstituteNoticePayload,
  InstituteNotice,
  InstituteNoticeList,
} from '@/types/instituteNotice';

/**
 * Every call the institute notice board makes — the office's own screen and the
 * card that previews it on a staff dashboard.
 *
 * As with the rest of the institute area there is no institute id in any path:
 * the server takes it from the caller's token, so there is nothing for a client
 * to point at another institute's board. Who may see which notices, and who may
 * take one down, is the server's decision too.
 *
 * The student panel reads the same board through `/student/notice-board`, which
 * lives with the rest of that panel's calls in `features/student/api`.
 */
export const instituteNoticesApi = {
  /**
   * The board, newest first.
   *
   * `onDate` is the day a notice is *about*, narrowed by the server rather than
   * by the browser: the board carries back the newest hundred, and an office
   * looking for what went up in March must still find it.
   */
  list: (onDate?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<InstituteNoticeList>>('/institute/notice-board', {
        params: { ...(onDate && { onDate }) },
      }),
    ),

  create: (payload: CreateInstituteNoticePayload) =>
    unwrap(apiClient.post<ApiSuccess<InstituteNotice>>('/institute/notice-board', payload)),

  /** Rewords a notice already up. The whole notice is sent, as the form holds it. */
  update: (uuid: string, payload: CreateInstituteNoticePayload) =>
    unwrap(
      apiClient.put<ApiSuccess<InstituteNotice>>(`/institute/notice-board/${uuid}`, payload),
    ),

  /** Pins it to the top of every board it appears on, or lets it fall back into date order. */
  setPinned: (uuid: string, pinned: boolean) =>
    unwrap(
      apiClient.patch<ApiSuccess<InstituteNotice>>(`/institute/notice-board/${uuid}/pin`, {
        pinned,
      }),
    ),

  /** A soft delete: what the institute told everybody is part of the year's record. */
  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/notice-board/${uuid}`)),
};
